import type { IFileItem } from '../models/FileItem';
import type { IFolderItem } from '../models/FolderItem';
import type { IBatchResult, IDryRunResult, IOperationLogEntry, OperationAction, OperationStatus } from '../models/Operation';
import type { IOrganizationPlan } from '../models/Plan';
import type { IScanResult } from '../models/ScanResult';
import { CHANGE_ORDER, type IResolvedChange } from '../plan/PlanSimulator';
import { filePath, values } from '../rules/ruleUtils';
import type { IAuditLog } from './AuditLog';
import { type IDriveItemRef, type IDriveWriter, NameConflictError } from './DriveWriter';

/** Destination given either as an existing folder, or as a library path to create on demand. */
type Destination = { parentId: string; path: string } | { ensurePath: string };

/** One atomic SharePoint operation a change expands into. */
interface IAtomicOp {
  changeId: string;
  action: OperationAction;
  itemId: string;
  itemKind: 'file' | 'folder';
  /** Where the item should be when the op runs; the op refuses to run if it isn't. */
  expectedParentId: string;
  expectedName: string;
  sourcePath: string;
  destination: Destination;
  destinationPath: string;
  newName: string;
  reason: string;
  confidence?: number;
  /**
   * Folders only: exact number of direct children the folder must have when the op runs
   * (0 for an empty folder; for a merge source, the items that travel with it). Anything
   * unexpected inside means the folder is left alone.
   */
  expectedChildCount?: number;
  /** Other folders that must hold exactly this many children (emptied sub-folders of a merge source). */
  verifyFolders?: { id: string; path: string; count: number }[];
  /** Indexes of ops that must all succeed first. */
  dependsOn?: number[];
  /**
   * Flattening: the pass-through folder being emptied. If it is no longer in this parent,
   * an earlier run already quarantined it, so the op is skipped rather than repeated.
   */
  onlyWhileFolderIn?: { folderId: string; parentId: string };
  /**
   * Index of the folder op this item travels with. No request is made: the item moves
   * because its folder does, and the log records it so every approved change is accounted for.
   */
  carriedBy?: number;
}

interface IOpOutcome {
  status: OperationStatus;
  error?: string;
  destinationParentId: string;
}

export interface IChangeEngineOptions {
  scan: IScanResult;
  plan: IOrganizationPlan;
  writer: IDriveWriter;
  audit: IAuditLog;
  /** Recorded as PerformedBy. */
  user: string;
  now?: () => Date;
  /** Random suffix for batch and operation IDs; injectable for tests. */
  idSuffix?: () => string;
}

function join(parent: string, name: string): string {
  if (!name) {
    return parent;
  }
  return parent ? `${parent}/${name}` : name;
}

function parentPathOf(path: string): string {
  const slash: number = path.lastIndexOf('/');
  return slash === -1 ? '' : path.substring(0, slash);
}

function folderLabelOf(folder: IFolderItem): string {
  return folder.path || folder.name;
}

function pad(n: number, width: number): string {
  let s: string = String(n);
  while (s.length < width) {
    s = `0${s}`;
  }
  return s;
}

function describeWriteError(error: unknown): string {
  if (error instanceof NameConflictError) {
    return error.message;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e: any = error;
  switch (e?.statusCode) {
    case 403: return 'You don\'t have permission to change this item.';
    case 404: return 'The item or its destination no longer exists.';
    case 423: return 'The item is locked — it may be open, checked out, or under a retention hold.';
    default: return String(e?.message ?? e ?? 'Unknown error');
  }
}

/**
 * Applies approved plan changes to a library. Safety properties:
 * - every operation is checked against live state first and refused if the item moved or
 *   was renamed since the scan, or if its destination name is taken (never overwrites);
 * - nothing is deleted: "quarantine" is a move into the plan's quarantine folder;
 * - a folder goes to quarantine only if it holds exactly what the plan expects;
 * - every operation, including failures and skips, is written to the audit log, and the
 *   batch stops immediately if the log can't be written;
 * - re-running a batch is safe: items already at their destination are skipped.
 */
export class ChangeEngine {
  private readonly _o: IChangeEngineOptions;

  constructor(options: IChangeEngineOptions) {
    this._o = options;
  }

  /** Checks each change against live SharePoint without writing anything. */
  public async dryRun(changes: IResolvedChange[]): Promise<IDryRunResult[]> {
    const { writer } = this._o;
    const ordered: IResolvedChange[] = this._ordered(changes);
    const ops: IAtomicOp[] = this._expand(ordered);

    // Simulated effects of earlier operations in the batch, so later checks see them.
    // In both maps '' means "known to be absent".
    const touched: { [itemId: string]: boolean } = {};
    const names: { [parentId: string]: { [lowerName: string]: string } } = {};
    const pathIds: { [path: string]: string } = {};
    const rememberPath = (path: string, id: string): void => { pathIds[path] = id; };

    const resolvePath = async (path: string): Promise<string | undefined> => {
      if (pathIds[path] !== undefined) {
        return pathIds[path] || undefined;
      }
      let current: IDriveItemRef | undefined = await writer.getRoot();
      for (const segment of path.split('/')) {
        current = current ? await writer.getChild(current.id, segment) : undefined;
      }
      rememberPath(path, current ? current.id : '');
      return current?.id;
    };

    const nameTaken = async (parentId: string, name: string, itemId: string): Promise<boolean> => {
      const overlay: { [lowerName: string]: string } | undefined = names[parentId];
      const key: string = name.toLowerCase();
      if (overlay && overlay[key] !== undefined) {
        return overlay[key] !== '' && overlay[key] !== itemId;
      }
      const live: IDriveItemRef | undefined = await writer.getChild(parentId, name);
      return !!live && live.id !== itemId;
    };

    const problems: { [changeId: string]: string[] } = {};
    const opCount: { [changeId: string]: number } = {};
    for (const r of ordered) {
      problems[r.change.id] = r.error ? [r.error] : [];
      opCount[r.change.id] = 0;
    }

    for (const op of ops) {
      opCount[op.changeId]++;
      const report = (message: string): void => {
        if (problems[op.changeId].indexOf(message) === -1) {
          problems[op.changeId].push(message);
        }
      };

      if (this._o.scan.protectedFolders[op.itemId]) {
        report(`"${op.sourcePath}" is protected: ${this._o.scan.protectedFolders[op.itemId]}`);
        continue;
      }
      if (!touched[op.itemId]) {
        const live: IDriveItemRef | undefined = await writer.getItem(op.itemId);
        if (!live) {
          report(`"${op.sourcePath}" no longer exists.`);
          continue;
        }
        if (live.parentId !== op.expectedParentId || live.name !== op.expectedName) {
          report(`"${op.sourcePath}" has been moved or renamed since the scan. Rescan the library.`);
          continue;
        }
        // Merge sources only reach their expected count after the batch's own moves.
        if (op.expectedChildCount !== undefined && !op.dependsOn && (live.childCount ?? 0) !== op.expectedChildCount) {
          report(`"${op.sourcePath}" is no longer empty. Rescan the library.`);
          continue;
        }
      }
      if (op.carriedBy !== undefined) {
        touched[op.itemId] = true;
        continue;
      }

      const parentId: string | undefined = 'parentId' in op.destination
        ? op.destination.parentId
        : await resolvePath(op.destination.ensurePath);
      if ('parentId' in op.destination && !(await writer.getItem(op.destination.parentId))) {
        report(`The destination folder "${op.destination.path}" no longer exists.`);
        continue;
      }
      if (parentId && await nameTaken(parentId, op.newName, op.itemId)) {
        report(`"${op.destinationPath}" already exists — the change would overwrite it.`);
        continue;
      }

      touched[op.itemId] = true;
      (names[op.expectedParentId] = names[op.expectedParentId] || {})[op.expectedName.toLowerCase()] = '';
      if (parentId) {
        (names[parentId] = names[parentId] || {})[op.newName.toLowerCase()] = op.itemId;
      }
    }

    return ordered.map(r => ({
      changeId: r.change.id,
      ready: problems[r.change.id].length === 0,
      operationCount: opCount[r.change.id],
      problems: problems[r.change.id]
    }));
  }

  /** Number of atomic operations the changes expand into (for progress reporting). */
  public countOperations(changes: IResolvedChange[]): number {
    return this._expand(this._ordered(changes)).length;
  }

  /**
   * Applies the changes in order, one operation at a time. Individual failures don't stop
   * the batch; an audit-log failure does.
   */
  public async apply(changes: IResolvedChange[], onProgress?: (done: number, total: number) => void): Promise<IBatchResult> {
    const { writer, audit, scan, user } = this._o;
    const now: () => Date = this._o.now ?? (() => new Date());
    const suffix: string = (this._o.idSuffix ?? (() => Math.random().toString(36).substring(2, 6).toUpperCase()))();
    const started: string = now().toISOString();
    const day: string = started.substring(0, 10).replace(/-/g, '');
    const batchId: string = `BATCH-${day}-${started.substring(11, 19).replace(/:/g, '')}-${suffix}`;
    const result: IBatchResult = { batchId, entries: [] };

    try {
      await audit.ensure();
    } catch (error) {
      result.abortedReason = `No changes were made. ${describeWriteError(error)}`;
      return result;
    }

    const ops: IAtomicOp[] = this._expand(this._ordered(changes));
    const statuses: OperationStatus[] = [];
    const pathIds: { [path: string]: string } = {};
    const rememberPath = (path: string, id: string): void => { pathIds[path] = id; };
    let seq: number = 0;
    let done: number = 0;

    const log = async (entry: Omit<IOperationLogEntry, 'operationId' | 'batchId' | 'user' | 'timestamp' | 'libraryId' | 'libraryName'>): Promise<void> => {
      seq++;
      const full: IOperationLogEntry = {
        ...entry,
        operationId: `OP-${day}-${suffix}-${pad(seq, 4)}`,
        batchId,
        libraryId: scan.library.id,
        libraryName: scan.library.name,
        user,
        timestamp: now().toISOString()
      };
      result.entries.push(full);
      await audit.write(full);
    };

    /** Finds or creates a folder path, logging every folder it creates. */
    const ensurePath = async (path: string): Promise<string> => {
      if (pathIds[path]) {
        return pathIds[path];
      }
      const parentPath: string = parentPathOf(path);
      const parentId: string = parentPath ? await ensurePath(parentPath) : (await writer.getRoot()).id;
      const name: string = path.substring(path.lastIndexOf('/') + 1);
      let folder: IDriveItemRef | undefined = await writer.getChild(parentId, name);
      if (!folder) {
        folder = await writer.createFolder(parentId, name);
        await log({
          changeId: '', action: 'createFolder', itemKind: 'folder', itemId: folder.id,
          sourcePath: '', sourceParentId: '', originalName: '',
          destinationPath: path, destinationParentId: parentId, newName: name,
          reason: path.indexOf('_Quarantine') === 0 ? 'Created to hold quarantined items.' : 'Created for the new folder structure.',
          status: 'success', reversible: true
        });
      } else if (!folder.isFolder) {
        throw new Error(`"${path}" exists but is a file, so items can't be quarantined there.`);
      }
      rememberPath(path, folder.id);
      return folder.id;
    };

    const runOp = async (op: IAtomicOp): Promise<IOpOutcome> => {
      let destinationParentId: string = 'parentId' in op.destination ? op.destination.parentId : '';

      if (op.carriedBy !== undefined) {
        return statuses[op.carriedBy] === 'failed'
          ? { status: 'failed', error: 'Left in place because its folder was not quarantined.', destinationParentId }
          : { status: statuses[op.carriedBy], error: 'Moved to quarantine together with its folder.', destinationParentId };
      }
      if (scan.protectedFolders[op.itemId]) {
        return { status: 'failed', error: `Protected: ${scan.protectedFolders[op.itemId]}`, destinationParentId };
      }
      if ((op.dependsOn ?? []).some(i => statuses[i] === 'failed')) {
        return {
          status: 'failed',
          error: 'Left in place because not everything could be moved out of it. Nothing inside it was changed.',
          destinationParentId
        };
      }

      const live: IDriveItemRef | undefined = await writer.getItem(op.itemId);
      if (!live) {
        throw new Error('The item no longer exists.');
      }
      if (op.onlyWhileFolderIn) {
        const guard: IDriveItemRef | undefined = await writer.getItem(op.onlyWhileFolderIn.folderId);
        if (!guard || guard.parentId !== op.onlyWhileFolderIn.parentId) {
          return { status: 'skipped', error: 'Already done (the folder was already flattened).', destinationParentId };
        }
      }
      if ('ensurePath' in op.destination) {
        destinationParentId = await ensurePath(op.destination.ensurePath);
      }
      if (live.parentId === destinationParentId && live.name === op.newName) {
        return { status: 'skipped', error: 'Already done (the item is already at its destination).', destinationParentId };
      }
      if (live.parentId !== op.expectedParentId || live.name !== op.expectedName) {
        throw new Error('The item was moved or renamed since the scan. Rescan the library and review again.');
      }
      if (op.expectedChildCount !== undefined && (live.childCount ?? 0) !== op.expectedChildCount) {
        throw new Error('The folder contains items the plan didn\'t expect, so it was left in place. Nothing inside it was changed.');
      }
      for (const check of op.verifyFolders ?? []) {
        const folder: IDriveItemRef | undefined = await writer.getItem(check.id);
        if (!folder || (folder.childCount ?? 0) !== check.count) {
          throw new Error(`"${check.path}" contains items the plan didn't expect, so the folder was left in place.`);
        }
      }
      await writer.moveItem(
        op.itemId,
        live.parentId !== destinationParentId ? destinationParentId : undefined,
        live.name !== op.newName ? op.newName : undefined
      );
      return { status: 'success', destinationParentId };
    };

    try {
      for (const op of ops) {
        let outcome: IOpOutcome;
        try {
          outcome = await runOp(op);
        } catch (error) {
          outcome = {
            status: 'failed',
            error: describeWriteError(error),
            destinationParentId: 'parentId' in op.destination ? op.destination.parentId : ''
          };
        }
        statuses.push(outcome.status);

        // An audit failure propagates and stops the batch.
        await log({
          changeId: op.changeId, action: op.action, itemKind: op.itemKind, itemId: op.itemId,
          sourcePath: op.sourcePath, sourceParentId: op.expectedParentId, originalName: op.expectedName,
          destinationPath: op.destinationPath, destinationParentId: outcome.destinationParentId, newName: op.newName,
          reason: op.reason, confidence: op.confidence, status: outcome.status, errorMessage: outcome.error,
          // Carried items are restored by undoing their folder's move.
          reversible: outcome.status === 'success' && op.carriedBy === undefined
        });
        done++;
        if (onProgress) {
          onProgress(done, ops.length);
        }
      }
    } catch (error) {
      result.abortedReason = `Stopped after ${done} of ${ops.length} operations because the audit log could not be written: ` +
        `${describeWriteError(error)} Changes already made are listed below.`;
    }

    return result;
  }

  private _ordered(changes: IResolvedChange[]): IResolvedChange[] {
    return changes.slice().sort((a, b) => CHANGE_ORDER.indexOf(a.change.type) - CHANGE_ORDER.indexOf(b.change.type));
  }

  /** Turns changes into atomic operations, from the scanned state. */
  private _expand(changes: IResolvedChange[]): IAtomicOp[] {
    const { scan, plan } = this._o;
    const ops: IAtomicOp[] = [];
    /** Where each item will be after the ops so far (only items moved directly). */
    const location: { [itemId: string]: { parentId: string; path: string } } = {};

    const quarantineDestination = (originalParentPath: string): { destination: Destination; path: string } => {
      const path: string = join(plan.quarantinePath, originalParentPath);
      return { destination: { ensurePath: path }, path };
    };

    const push = (op: IAtomicOp): number => {
      ops.push(op);
      if ('parentId' in op.destination) {
        location[op.itemId] = { parentId: op.destination.parentId, path: op.destination.path };
      }
      return ops.length - 1;
    };

    const baseOp = (item: IFileItem | IFolderItem, kind: 'file' | 'folder', changeId: string, action: OperationAction,
      reason: string, confidence: number | undefined): Omit<IAtomicOp, 'destination' | 'destinationPath' | 'newName'> => {
      const now: { parentId: string; path: string } | undefined = location[item.id];
      const itemPath: string = kind === 'file' ? filePath(item as IFileItem) : (item as IFolderItem).path;
      return {
        changeId, action, itemId: item.id, itemKind: kind,
        expectedParentId: now ? now.parentId : item.parentId!,
        expectedName: item.name,
        sourcePath: now ? join(now.path, item.name) : itemPath,
        reason, confidence
      };
    };

    // Which changes quarantine which items, and which folders a merge will quarantine.
    const quarantineChange: { [itemId: string]: IResolvedChange } = {};
    const mergeSources: { [folderId: string]: boolean } = {};
    for (const r of changes) {
      if (r.change.type === 'quarantineFile' || r.change.type === 'quarantineFolder') {
        quarantineChange[r.change.targetId] = r;
      }
      if (r.change.type === 'mergeFolders') {
        for (const id of r.sourceFolderIds!) {
          mergeSources[id] = true;
        }
      }
    }

    /**
     * The outermost merge source an item sits in, if any. Quarantined items inside a merge
     * source stay put and travel with it; moving them separately would create a quarantine
     * folder with the source's own name and block it.
     */
    const insideMergeSource = (parentId: string | undefined): string | undefined => {
      let id: string | undefined = parentId;
      let found: string | undefined;
      while (id && id !== scan.rootId) {
        if (mergeSources[id]) {
          found = id;
        }
        id = scan.folders[id].parentId;
      }
      return found;
    };

    const carried: { [sourceId: string]: IResolvedChange[] } = {};

    const emptyDescendants = (folder: IFolderItem): { id: string; path: string; count: number }[] => {
      const out: { id: string; path: string; count: number }[] = [];
      for (const id of folder.childFolderIds) {
        const child: IFolderItem = scan.folders[id];
        out.push({ id, path: child.path, count: child.childFolderIds.length });
        out.push(...emptyDescendants(child));
      }
      return out;
    };

    /** Moves source's contents into target. Returns how many direct children stay in source. */
    const mergeInto = (source: IFolderItem, target: IFolderItem, r: IResolvedChange,
      moveOps: number[], verify: { id: string; path: string; count: number }[]): number => {
      const c = r.change;
      const targetChildFolders: { [lowerName: string]: IFolderItem } = {};
      for (const id of target.childFolderIds) {
        targetChildFolders[scan.folders[id].name.toLowerCase()] = scan.folders[id];
      }
      const destination: Destination = { parentId: target.id, path: target.path };
      let remaining: number = 0;

      for (const id of source.childFolderIds) {
        const child: IFolderItem = scan.folders[id];
        if (quarantineChange[id]) {
          remaining++;
          continue;
        }
        const same: IFolderItem | undefined = targetChildFolders[child.name.toLowerCase()];
        if (same) {
          // Same-named subfolder in the target: merge into it; the emptied child stays behind.
          verify.push({ id: child.id, path: child.path, count: mergeInto(child, same, r, moveOps, verify) });
          remaining++;
        } else {
          moveOps.push(push({ ...baseOp(child, 'folder', c.id, 'move', c.reason, c.confidence),
            destination, destinationPath: join(target.path, child.name), newName: child.name }));
        }
      }
      for (const id of source.fileIds) {
        const file: IFileItem = scan.files[id];
        if (quarantineChange[id]) {
          remaining++;
          continue;
        }
        moveOps.push(push({ ...baseOp(file, 'file', c.id, 'move', c.reason, c.confidence),
          destination, destinationPath: join(target.path, file.name), newName: file.name }));
      }
      return remaining;
    };

    for (const r of changes) {
      const c = r.change;
      switch (c.type) {
        case 'quarantineFile':
        case 'quarantineFolder': {
          const item: IFileItem | IFolderItem = c.type === 'quarantineFile' ? scan.files[c.targetId] : scan.folders[c.targetId];
          const source: string | undefined = insideMergeSource(item.parentId);
          if (source) {
            (carried[source] = carried[source] || []).push(r);
            break;
          }
          const originalParentPath: string = c.type === 'quarantineFile'
            ? (item as IFileItem).parentPath
            : parentPathOf((item as IFolderItem).path);
          const q = quarantineDestination(originalParentPath);
          const folder: IFolderItem | undefined = c.type === 'quarantineFolder' ? item as IFolderItem : undefined;
          push({
            ...baseOp(item, c.itemKind, c.id, 'quarantine', c.reason, c.confidence),
            destination: q.destination, destinationPath: join(q.path, item.name), newName: item.name,
            // An empty branch moves whole; every level must still hold only its empty subfolders.
            expectedChildCount: folder ? folder.childFolderIds.length : undefined,
            verifyFolders: folder ? emptyDescendants(folder) : undefined
          });
          break;
        }
        case 'mergeFolders': {
          const target: IFolderItem = scan.folders[r.targetFolderId!];
          for (const sourceId of r.sourceFolderIds!) {
            const source: IFolderItem = scan.folders[sourceId];
            const moveOps: number[] = [];
            const verify: { id: string; path: string; count: number }[] = [];
            const remaining: number = mergeInto(source, target, r, moveOps, verify);

            const q = quarantineDestination(parentPathOf(source.path));
            const folderOp: number = push({
              ...baseOp(source, 'folder', c.id, 'quarantine', `Emptied by consolidating into "${target.path}".`, c.confidence),
              destination: q.destination, destinationPath: join(q.path, source.name), newName: source.name,
              expectedChildCount: remaining,
              verifyFolders: verify,
              dependsOn: moveOps
            });
            for (const rc of carried[sourceId] ?? []) {
              const isFile: boolean = rc.change.itemKind === 'file';
              const item: IFileItem | IFolderItem = isFile ? scan.files[rc.change.targetId] : scan.folders[rc.change.targetId];
              const itemPath: string = isFile ? filePath(item as IFileItem) : (item as IFolderItem).path;
              push({
                ...baseOp(item, rc.change.itemKind, rc.change.id, 'quarantine', rc.change.reason, rc.change.confidence),
                destination: q.destination, destinationPath: join(plan.quarantinePath, itemPath), newName: item.name,
                carriedBy: folderOp
              });
            }
          }
          break;
        }
        case 'moveFolder': {
          const folder: IFolderItem = scan.folders[c.targetId];
          const parentPath: string = r.parentPath ?? '';
          const parent: IFolderItem | undefined = parentPath
            ? values(scan.folders).filter(f => f.path.toLowerCase() === parentPath.toLowerCase())[0]
            : scan.folders[scan.rootId];
          if (parent) {
            push({ ...baseOp(folder, 'folder', c.id, 'move', c.reason, c.confidence),
              destination: { parentId: parent.id, path: parent.path },
              destinationPath: join(parent.path, folder.name), newName: folder.name });
          }
          break;
        }
        case 'restructure': {
          // Into a category folder that may not exist yet: created (and logged) on demand.
          const parentPath: string = r.parentPath ?? '';
          const item: IFileItem | IFolderItem = c.itemKind === 'file' ? scan.files[c.targetId] : scan.folders[c.targetId];
          push({ ...baseOp(item, c.itemKind, c.id, 'move', c.reason, c.confidence),
            destination: { ensurePath: parentPath },
            destinationPath: join(parentPath, item.name), newName: item.name });
          break;
        }
        case 'flattenFolders': {
          for (const id of c.collapseFolderIds ?? []) {
            const passThrough: IFolderItem = scan.folders[id];
            const child: IFolderItem = scan.folders[passThrough.childFolderIds[0]];
            const now: { parentId: string; path: string } | undefined = location[id];
            const parentId: string = now ? now.parentId : passThrough.parentId!;
            const parentPath: string = now ? now.path : parentPathOf(passThrough.path);
            const moveIndex: number = push({ ...baseOp(child, 'folder', c.id, 'move', c.reason, c.confidence),
              destination: { parentId, path: parentPath },
              destinationPath: join(parentPath, child.name), newName: child.name,
              onlyWhileFolderIn: { folderId: passThrough.id, parentId } });
            const q = quarantineDestination(parentPathOf(passThrough.path));
            push({
              ...baseOp(passThrough, 'folder', c.id, 'quarantine', `Emptied by flattening "${folderLabelOf(passThrough)}".`, c.confidence),
              destination: q.destination, destinationPath: join(q.path, passThrough.name), newName: passThrough.name,
              expectedChildCount: 0,
              dependsOn: [moveIndex]
            });
          }
          break;
        }
        case 'fileIntake': {
          const file: IFileItem = scan.files[c.targetId];
          const parent: IFolderItem | undefined = values(scan.folders)
            .filter(f => f.path.toLowerCase() === (r.parentPath ?? '').toLowerCase())[0];
          if (parent) {
            push({ ...baseOp(file, 'file', c.id, 'move', c.reason, c.confidence),
              destination: { parentId: parent.id, path: parent.path },
              destinationPath: join(parent.path, file.name), newName: file.name });
          }
          break;
        }
        case 'renameFile': {
          const file: IFileItem = scan.files[c.targetId];
          // Renames run last: if a merge moved the file, rename it in its new folder.
          const base = baseOp(file, 'file', c.id, 'rename', c.reason, c.confidence);
          const folderPath: string = location[file.id] ? location[file.id].path : file.parentPath;
          push({ ...base, destination: { parentId: base.expectedParentId, path: folderPath },
            destinationPath: join(folderPath, r.newName!), newName: r.newName! });
          break;
        }
      }
    }
    return ops;
  }
}
